import { ApiRequestError, Skeleton } from '@h2hspark/ui';
import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageBackLink } from '@/member/page-back-link';

import { useAttendeeProfile } from './api';
import { AttendeeDetailCard } from './attendee-detail-card';
import { AttendeeDetailCloseControl, AttendeeDetailOverlay } from './attendee-detail-dialog';

const BACK_TO_ROSTER_LABEL = "Back to who's going";

/**
 * `MEM-012` — the attendee profile, at its own address and as `Web App.dc.html` §2.16's overlay.
 *
 * ## What this file is, after 2026-09-12
 *
 * Almost nothing. The screen **is** `AttendeeDetailCard`, which is §2.16 element for element and
 * carries every note about what it draws and where it departs from the prototype. This file is the
 * two ways in: {@link AttendeeProfilePage}, the standalone route, and
 * {@link AttendeeProfileOverlay}, the same card over the list a member opened it from.
 *
 * Until that day this file held the screen itself, and the screen was the old treatment Sumit
 * asked about — a 200px full-bleed gradient band, two full-width stacked outline buttons with a
 * helper sentence under each, and an About region that said *"{First} has not written an About
 * yet"* whenever the paragraph alone was missing. *"attendee profile is not cleaned up"*
 * (2026-09-12, ruling 64).
 *
 * ## The route stays, and both entry points are real
 *
 * `MEM-047` mails links to this address and `MEM-012` AC-1 makes it the event-scoped view of one
 * member; a bookmark of it keeps working, and so does a reload. The list surfaces pass their own
 * location in `state.backgroundLocation`, and `App.tsx` renders the overlay for those — see
 * `attendee-detail-dialog.tsx`.
 */
export function AttendeeProfilePage(): React.ReactElement {
  const { eventId = '', attendeeRef = '' } = useParams<{
    eventId: string;
    attendeeRef: string;
  }>();

  return (
    /* Full-bleed on a phone, where the card is the screen; a centred 460px card from `md`, which
       is §2.16's own width. `MemberLayout` gives the page 16px of side padding and 24px of top,
       and the gradient header has to reach the edges below `md`. */
    <div className="-mx-4 -mt-6 md:mx-auto md:mt-0 md:w-full md:max-w-[460px]">
      <AttendeeProfileBody
        eventId={eventId}
        attendeeRef={attendeeRef}
        headingLevel="h1"
        cardClassName="md:rounded-xl md:border md:border-border-subtle md:shadow-sm"
        control={
          <PageBackLink
            variant="floating"
            to={`/app/events/${encodeURIComponent(eventId)}/attendees`}
          >
            {BACK_TO_ROSTER_LABEL}
          </PageBackLink>
        }
      />
    </div>
  );
}

/** The same card, over the list it was opened from (`MEM-056` AC-3). */
export function AttendeeProfileOverlay(): React.ReactElement {
  const { eventId = '', attendeeRef = '' } = useParams<{
    eventId: string;
    attendeeRef: string;
  }>();
  const navigate = useNavigate();
  const close = React.useCallback(() => {
    /* One step of the member's own history, which is the list underneath at the scroll position
       they left it — never a path typed here, because this overlay opens from the roster, the
       Matches page and, when `MEM-030` lands, the in-event roster. */
    navigate(-1);
  }, [navigate]);

  return (
    <AttendeeDetailOverlay title={BACK_TO_ROSTER_LABEL} onClose={close}>
      <AttendeeProfileBody
        eventId={eventId}
        attendeeRef={attendeeRef}
        headingLevel="h2"
        control={<AttendeeDetailCloseControl onClose={close} backLabel={BACK_TO_ROSTER_LABEL} />}
      />
    </AttendeeDetailOverlay>
  );
}

/** The read and its three states, shared by the route and the overlay so they cannot disagree. */
function AttendeeProfileBody({
  eventId,
  attendeeRef,
  control,
  headingLevel,
  cardClassName,
}: {
  readonly eventId: string;
  readonly attendeeRef: string;
  readonly control: React.ReactNode;
  readonly headingLevel: 'h1' | 'h2';
  readonly cardClassName?: string | undefined;
}): React.ReactElement {
  const query = useAttendeeProfile(eventId, attendeeRef === '' ? null : attendeeRef, {
    enabled: true,
  });

  if (query.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[196px] w-full" />
        <div className="space-y-3 px-6">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  const profile = query.data?.profile;
  if (query.error !== null || profile === undefined) {
    const notFound = query.error instanceof ApiRequestError && query.error.statusCode === 404;
    return (
      <div className="space-y-4 p-6">
        <Link
          to={`/app/events/${encodeURIComponent(eventId)}`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
          <span>Back to event</span>
        </Link>
        <h1 className="text-2xl">
          {notFound ? 'That person is not on this list' : 'We could not open that profile'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {notFound
            ? // The same message for somebody who chose not to appear, somebody who cancelled and
              // a reference that never named anybody — telling them apart would be the disclosure
              // the private election exists to prevent.
              'They may have chosen not to appear in the preview, or they may no longer be coming.'
            : 'Something went wrong on our side. Try again in a moment.'}
        </p>
      </div>
    );
  }

  return (
    <AttendeeDetailCard
      eventId={eventId}
      profile={profile}
      /* An attendee detail is not a match. `MEM-032` AC-4 pre-fills *from a match, with the match
         context*, and writing "we matched" here would be a claim the platform cannot support —
         this member may never have been suggested to them at all. */
      narrative={null}
      prefilledMessage={null}
      control={control}
      headingLevel={headingLevel}
      className={cardClassName}
    />
  );
}
